import { BookingEntity } from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface BookingDirectionChangeState {
	/**
	 * A discounted booking is held in `restrictedMode`, which stops the customer reducing what they
	 * booked — the discount was calculated against the original booking and doesn't reliably survive
	 * a re-quote. Changing route re-prices from scratch, so it's the same problem, and the change
	 * stays unavailable on a discounted booking until that's solved properly.
	 */
	restrictedMode?: boolean;
	/**
	 * The booking is partially checked in — `checkedIn`, or somewhere on the check-in ladder. Staff
	 * can still edit such a booking (`canModifyBooking` returns early for staff), so this has to be
	 * checked separately rather than relying on the edit gate.
	 *
	 * Check-in statuses belong to a route (`FK_CheckInStatus_Route_RouteId`) and nothing on the
	 * alteration path rewrites `CheckInStatusId`, so a booking moved to another route mid-check-in
	 * would carry a status from a ladder that no longer applies to it.
	 */
	checkInStarted?: boolean;
}

/**
 * True when any leg of the booking has started its check-in ladder.
 *
 * Both flags are asked of the PAIR rather than of one leg, because the backend guard is per-leg
 * (`DetectJourneyChangeStep` checks `OriginalBooking` and `OriginalBooking.ReturnBooking`
 * separately) and an edit applied to both trips submits both. Asking only the departure leg would
 * offer the control, enable the selector, and then answer 400 at the end of the flow.
 */
export function anyLegCheckInStarted(...legs: (BookingEntity | null | undefined)[]): boolean {
	return legs.some(leg => leg?.checkedIn === true || isNotNullOrUndefined(leg?.checkInStatusId));
}

/**
 * True when any leg of the booking carries a discount. See `restrictedMode` above.
 *
 * Both kinds count. `bookedSummary.discountAmount` sums only `alteration.Discounts` server-side
 * (`FerryBookingSummariesService.CalculateSummaryPrices`), so a manager discount — which lives in
 * the separate `ManagerBookingDiscounts` collection and is applied against a specific booking the
 * same way — is invisible to it. Read off the booking's alterations instead so both are caught.
 */
export function anyLegDiscounted(...legs: (BookingEntity | null | undefined)[]): boolean {
	return legs.some(
		leg => (leg?.bookedSummary?.discountAmount ?? 0) > 0 || hasManagerDiscount(leg),
	);
}

function hasManagerDiscount(booking: BookingEntity | null | undefined): boolean {
	return (booking?.alterations ?? []).some(alteration =>
		(alteration.managerBookingDiscounts ?? []).some(discount => (discount.discountAmount ?? 0) > 0),
	);
}

/**
 * Whether the acting user may move an existing booking to a different journey while editing it:
 * a different route, the opposite direction, or a different pair of stops on the same route.
 *
 * Available to customers and staff alike — this is the change people ring up about, and the
 * self-service path is the point of the feature.
 *
 * Mirrors `DetectJourneyChangeStep` on the backend, which is the real boundary — this only decides
 * whether the control is offered. Note the backend also skips the whole question inside check-in,
 * where the journey isn't editable anyway (the start stop is locked and there is no route selector)
 * but the disembark stop is, deliberately.
 */
export function canChangeBookingDirection({
	restrictedMode = false,
	checkInStarted = false,
}: BookingDirectionChangeState = {}): boolean {
	return !restrictedMode && !checkInStarted;
}

/**
 * Why this booking's route can't be changed, or undefined when it can.
 *
 * The single derivation for both places that need it — the edit modal's option list and the wizard's
 * route selector. They have to agree: "Date or time" lands on the same wizard tab the route selector
 * lives on, so a booking the modal refuses would otherwise arrive at an enabled selector.
 *
 * Worded for the person reading it rather than after the flag that produced it. A customer told
 * "discounted bookings can't be changed" can act on it; a missing control can only be escalated,
 * which is the whole point — the family booked in the wrong direction is the call this is meant to
 * stop.
 */
export function getJourneyChangeBlockedReason(
	departureBooking: BookingEntity | null | undefined,
	returnBooking: BookingEntity | null | undefined,
): string | undefined {
	// Check-in first: it's the more specific of the two, and a booking can be both.
	if (anyLegCheckInStarted(departureBooking, returnBooking)) {
		return "This booking has already started check in, so its route can't be changed. Speak to staff at the terminal.";
	}

	if (anyLegDiscounted(departureBooking, returnBooking)) {
		return "Bookings with a discount applied can't have their route changed. Contact us and we'll sort it out for you.";
	}

	return undefined;
}
